import { Navigate, Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "./AdminAuth";
import { LayoutDashboard, Users, Boxes, Building2, Download, LogOut, Truck, Menu, X, Network, Send, Ship } from "lucide-react";
import { useState } from "react";

export const AdminGuard = ({ children }) => {
  const { user } = useAdminAuth();
  const loc = useLocation();
  if (user === undefined) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0f1512] text-white">
        <div className="text-sm text-white/60">Checking session…</div>
      </div>
    );
  }
  if (user === null) return <Navigate to="/admin/login" state={{ from: loc }} replace />;
  return children;
};

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/freight-quotes", label: "Freight Quotes", icon: Ship },
  { to: "/admin/sources", label: "Sources", icon: Boxes },
  { to: "/admin/hubs", label: "Hub Connections", icon: Network },
  { to: "/admin/merchants", label: "Merchants", icon: Building2 },
  { to: "/admin/outbox", label: "Outbox", icon: Send },
  { to: "/admin/import", label: "Import", icon: Download },
];

export const AdminLayout = () => {
  const { user, logout } = useAdminAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0b1210] text-white flex">
      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-30 w-64 bg-[#0f1512] border-r border-white/10 flex flex-col transition-transform ${open ? "" : "-translate-x-full md:translate-x-0"}`}>
        <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-[var(--tt-green)] grid place-items-center">
              <Truck className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-display font-bold leading-tight">Turbo BD</div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Console</div>
            </div>
          </div>
          <button className="md:hidden text-white/60" onClick={() => setOpen(false)} data-testid="admin-close-nav">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-[var(--tt-green)] text-white"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon className="h-4 w-4" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 text-xs text-white/50">
            <div className="truncate">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-widest mt-0.5">{user?.role}</div>
          </div>
          <button
            data-testid="admin-logout"
            onClick={async () => { await logout(); nav("/admin/login"); }}
            className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/60 hover:bg-red-500/10 hover:text-red-400 transition"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-20 bg-[#0f1512] border-b border-white/10 px-4 h-14 flex items-center justify-between">
          <button onClick={() => setOpen(true)} className="text-white/70"><Menu className="h-5 w-5" /></button>
          <div className="text-sm font-semibold">Turbo BD Console</div>
          <div className="w-5" />
        </header>
        <div className="p-5 md:p-8 max-w-7xl">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
